import { useState } from "react";
import { createRole, deleteRole } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";

export function ManageRolesDialog({ open, onOpenChange, roles, onRefresh }) {
  const [newRole, setNewRole] = useState("");

  const handleAdd = async () => {
    const name = newRole.trim();
    if (!name) return;
    try {
      await createRole(name);
      toast.success(`Role "${name}" added`);
      setNewRole("");
      onRefresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add role");
    }
  };

  const handleDelete = async (roleId, roleName) => {
    try {
      await deleteRole(roleId);
      toast.success(`Role "${roleName}" removed`);
      onRefresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to remove role");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-card border-border max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="uppercase tracking-wider" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Manage Roles</DialogTitle>
          <DialogDescription className="text-muted-foreground text-xs">Add or remove crew roles available for assignment</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="flex items-center gap-2">
            <Input
              data-testid="new-role-input"
              placeholder="New role name..."
              value={newRole}
              onChange={e => setNewRole(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleAdd()}
              className="h-8 text-sm bg-secondary/30 border-border flex-1"
            />
            <Button
              data-testid="add-role-btn"
              size="sm"
              className="h-8 bg-primary text-primary-foreground hover:bg-primary/90 text-xs"
              onClick={handleAdd}
            >
              <Plus className="w-3 h-3 mr-1" /> Add
            </Button>
          </div>
          <div className="space-y-1 max-h-72 overflow-y-auto">
            {roles.map(r => (
              <div
                key={r.role_id}
                data-testid={`role-item-${r.role_id}`}
                className="flex items-center justify-between py-1.5 px-2 rounded-sm hover:bg-secondary/20"
              >
                <span className="text-sm">{r.name}</span>
                <button
                  data-testid={`delete-role-${r.role_id}`}
                  onClick={() => handleDelete(r.role_id, r.name)}
                  className="text-muted-foreground hover:text-red-400 p-1"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
